import UpdateAccessDialogHtml from "./UpdateAccessDialogHtml.js";
import Team from "../../js/entities/Team.js";
import Permission from "../../js/components/permission/Permission.js";
import SidDropdownItem from "./SidDropdownItem.js";
import AccessLevel from "../../js/entities/AccessLevel.js";

export default class UpdateAccessDialogCmp {
    /** @type {TeamRepository} */
    #teamRepo;
    /** @type {UserRepository} */
    #userRepo;
    /** @type {string} */
    #orgId;
    /** @type {SidDropdownItem[]} */
    #teamsPermissions = [];
    /** @type {UpdateAccessDialogHtml} */
    #html;
    /** @type {ResourcePermission[]} */
    #resourcePermissions;
    #onSave = () =>{}

    /**
     * @param {TeamRepository} teamRepo
     * @param {UserRepository} userRepo
     * @param {string} orgId
     * @param {string} currentUserId
     */
    constructor(teamRepo, userRepo, orgId, currentUserId) {
        this.#teamRepo = teamRepo;
        this.#userRepo = userRepo;
        this.#orgId = orgId;
        this.#html = new UpdateAccessDialogHtml(currentUserId);
    }

    /** @param {ResourcePermission[]} resourcePermissions */
    show(resourcePermissions) {
        this.#resourcePermissions = resourcePermissions;
        this.#html.show(this.createArrayOfResourcePermissionForDisplaying(resourcePermissions), resourcePermissions[0].isBatchPermission());
        this.#html.onSaveCb(this.#onSaveClick.bind(this));
        this.#html.onPermissionChangeCb(this.#onPermissionChange.bind(this));
        this.#html.onPermissionDeleteCb(this.#onPermissionDelete.bind(this));
        this.#html.onPermissionAddCb(this.#onPermissionAdd.bind(this));
        this.#html.onCheckIsSubjectInList(this.#isSubjectInList.bind(this));
        this.#html.onSubjectClick(this.#onSubjectInputClick.bind(this));
        this.#html.onSubjectSearch(this.#onSubjectSearch.bind(this));
    }

    remove() {
        this.#html.remove()
    }

    /** @param {function} callback */
    onSaveCb(callback) {
        this.#onSave = callback;
    }

    /** @returns {ResourcePermission[]} */
    getResourcePermissions() {
        return this.#resourcePermissions;
    }

    /**
     * Public for tests.
     * @param {ResourcePermission[]} resourcePermissions
     */
    createArrayOfResourcePermissionForDisplaying(resourcePermissions){
        const uniquePermissions = UpdateAccessDialogCmp.#permissionsMappedBySid(resourcePermissions);
        return UpdateAccessDialogCmp.#sortPermissions(uniquePermissions)
    }

    /** @param {boolean} isVisible */
    toggleProgressBarVisibility(isVisible){
        this.#html.toggleProgressBarVisibility(isVisible)
    }

    /**
     * In case of bulk editing of permissions, it's possible that the same SID has <i>different</i> access levels
     * for different resources. If that's the case, we can't show any particular access level and want to show
     * "Permissions differ" - for this we put {@link AccessLevel#NONE} instead of actual access level.
     *
     * @param {ResourcePermission[]} resourcePermissions
     * @returns {Map<string, Permission>} where key is the SID
     */
    static #permissionsMappedBySid(resourcePermissions){
        const result = new Map();
        for (const rp of resourcePermissions) {
            for (const permission of rp.permissions.values()){
                result.set(permission.sid, permission.createCopy());
            }
        }
        for (const permission of result.values()) {
            for (const rp of resourcePermissions) {
                const p = rp.permissions.get(permission.sid);
                // Two scenarios:
                // - p is "Not found",  meaning one of the _next_ permissions in the map has the access level while
                //   current entry doesn't. So they differ.
                // - if permission found but is different from the previous value, it means there are still 2
                //   different access levels
                // In both cases we want to show "Permissions differ", so setting them to NONE
                if(!p || p.accessLevel.intValue() !== permission.accessLevel.intValue())
                    permission.accessLevel = AccessLevel.NONE;
            }
        }
        return result;
    }

    /**
     * @param {Map<string, Permission>} permissions
     * @returns {Permission[]}
     */
    static #sortPermissions(permissions){
        const teams = [];
        const users = [];
        let publicTeamPermission = null;
        permissions.forEach( value => {
            if (value.isTeamSid() && value.subject.isPublicTeam()){
                // we want to separate team with id === "p" (public team)
                publicTeamPermission = value;
            } else if (value.isTeamSid()) {
                teams.push(value);
            } else {
                users.push(value);
            }
        })
        teams.sort((a, b) => a.name.localeCompare(b.name));
        users.sort((a, b) => a.name.localeCompare(b.name));
        // we want Public team to go first because it may be the most important info - e.g. if user
        // accidentally shared private resource with the whole Internet
        if(publicTeamPermission)
            return [publicTeamPermission, ...teams, ...users];
        else return [...teams, ...users]
    }

    /**
     * @param {UserJson|Team} subject
     * @returns {boolean}
     */
    #isSubjectInList(subject){
        for (const rp of this.#resourcePermissions){
            if (rp.permissions.has(subject.id)){
                return true
            }
        }
        return false
    }

    /** @param {string} name */
    async #onSubjectSearch(name) {
        const users = name ? await this.#userRepo.getMatchedUsers(name, null, this.#orgId) : [];
        const userPermissions = [];
        for (const user of users){
            userPermissions.push(new SidDropdownItem(Permission.SUBJECT_TYPE.USER, user, !this.#isSubjectInList(user)));
        }
        userPermissions.sort((a, b) => a.name.localeCompare(b.name))
        const matchedTeamsPermissions = this.#teamsPermissions
            .filter(a => a.name.toLowerCase().startsWith(name.toLowerCase()));
        this.#html.setDropdownItems([...matchedTeamsPermissions, ...userPermissions]);
    }

    async #onSubjectInputClick() {
        if (!this.#teamsPermissions.length){
            const teams = await this.#teamRepo.getTeamsByOrgId(this.#orgId);
            for (const team of teams){
                if (team.isAdminTeam())
                    continue
                this.#teamsPermissions.push(new SidDropdownItem(Permission.SUBJECT_TYPE.TEAM, team, !this.#isSubjectInList(team)));
            }
            this.#teamsPermissions.sort((a, b) => a.name.localeCompare(b.name));
            // We add public team manually because it is Global Team, we don't get it in the list of org teams
            const publicTeam = Team.createPublicTeam();
            this.#teamsPermissions.unshift(new SidDropdownItem(Permission.SUBJECT_TYPE.TEAM, publicTeam, !this.#isSubjectInList(publicTeam)));
        }
        this.#html.setDropdownItems(this.#teamsPermissions);
    }

    /**
     * @param {Permission} permission
     * @param {accessLevel: AccessLevel, displayName: string, title: string} inputOptions
     */
    #onPermissionChange(permission, inputOptions){
        if (!inputOptions)
            return
        permission.accessLevel = inputOptions.accessLevel;
        this.#resourcePermissions.forEach(resource => {
            resource.permissions.set(permission.sid, permission.createCopy());
        })
    }

    /** @param {Permission} permission */
    #onPermissionDelete(permission){
        this.#resourcePermissions.forEach(resource => {
            resource.permissions.delete(permission.sid);
        })
    }

    /**
     * @param {SidDropdownItem} sidDropdownItem
     * @param {AccessLevel} accessLevel
     */
    #onPermissionAdd(sidDropdownItem, accessLevel){
        const permission = new Permission(sidDropdownItem.type, sidDropdownItem.subject, accessLevel)
        this.#resourcePermissions.forEach(resource =>
            resource.permissions.set(permission.sid, permission)
        )
        this.#html.addNewPermission(permission);
    }

    /**
     * User can share permissions on batch only (action {@link PermissionRepository.SAVE_ACTION_TYPE.SAVE})
     * or cascade these permissions on injections too (action {@link PermissionRepository.SAVE_ACTION_TYPE.SAVE_NESTED})
     * @param {string} actionType
     */
    async #onSaveClick(actionType){
        await this.#onSave(this.#resourcePermissions, actionType);
        this.remove();
    }
}